import axios from "axios";
import { Avatar } from "components/avatar";
import TableComponent from "components/table";
import Switch from "rc-switch";
import {useState, useEffect} from "react";
import { useQuery } from "react-query";
import { dollarFormatter } from "utils";
import { useTranslation } from 'react-i18next';

type Props = {
  type: "project";
  id: string;
};

type TQuery = {
  items:
    | {
        label: string;
        id: number;
        estimate_qty: number;
        used_qty: number;
        price: number;
        product_id: number;
        item_id: number;
      }[]
    | null;
  users: { user: string; fk_user_id: number; sum: number }[] | null;
};

export default function ProductionContainer(props: Props) {
  const { t } = useTranslation();
  const tBase = "containers.productionContainer.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const [hide, setHide] = useState(false);

  const query = useQuery<TQuery>(
    ["production", props.type, props.id],
    async () =>
      await axios
        .get(
        `
          ${process.env.REACT_APP_SERVER_URL}/api/v1/company/production/${props.id}?type=${props.type}
        `
        )
        .then((res) => res.data)
  );

  // gets appointments for a project
  const getApptsForProjects = useQuery(
    ["appointments"],
    async () =>
        await axios
            .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/appointment/projects/${props.id}`)
            .then((res: any) => res.data)
  );

  // taken from useAppointmentProduction.tsx, mostly the same
  const fetchAppointmentProduction = async (appointmentIds: number[]) => {
      try {
          const results = await Promise.all(
              appointmentIds.map(async (appointmentId) => {
                  const response = await axios.get(
                      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/metrics/production/${appointmentId}?type=appointment`
                  );
                  return { appointmentId, data: response.data };
              })
          );
          return results;
      } catch (error) {
          console.error("Error fetching appointment production data:", error);
          throw error;
      }
  };

  const [productionData, setProductionData] = useState<any>([]);

  useEffect(() => {
    // Ensure data is available before accessing `appointments`
    if (!getApptsForProjects.data || !Array.isArray(getApptsForProjects.data?.appointments)) {
      return;
    }
    
    const fetchData = async () => {
        const appointmentIds = getApptsForProjects.data.appointments.map((appt: any) => appt.id);
        const results = await fetchAppointmentProduction(appointmentIds);
        setProductionData(results);
    };

    fetchData();

  }, [getApptsForProjects.data]);


  const [userData, setUserData] = useState<any>([]);

  useEffect(() => {
    if (!productionData || productionData.length === 0) return;

    setUserData((prevUserData: any[]) => {
      const updatedUserData = [...prevUserData];

      productionData.forEach((production: any) => {
        if (!production?.data?.data) return;
        
        production.data.data.forEach((item: any) => {
          const user_name = `${item.user.first_name} ${item.user.last_name}`;
          const fk_user_id = item.fk_user_id;
          const sum = +item.appointment_production;

          // Find existing user, if found, update the sum, else add a new user
          const userIndex = updatedUserData.findIndex((user) => user.fk_user_id === fk_user_id);

          if (userIndex !== -1) {
            updatedUserData[userIndex].sum += sum;
          } else {
            updatedUserData.push({
              user: user_name,
              fk_user_id: fk_user_id,
              sum: sum,
            });
          }
        });
      });

      return updatedUserData;
    });

  }, [productionData]);


  const columns = {
    label: {
      label: tr("Label"),
      width: "100%",
      cell: (row: any) => (
        <div>
          <span className="text-bold"> {row.label}</span>
          <span className="text-xs text-slate-500 font-thin ml-2">
            (
            {row.item_id
              ? `${tr("Part ID")}: ${row.item_id}`
              : `${tr("Service ID")}: ${row.product_id}`}
            )
          </span>
        </div>
      ),
    },
    estimate_price: {
      label: tr("Estimate Total"),
      cell: (row: any) => dollarFormatter(row.estimate_qty * row.price),
    },
    estimate_qty: {
      label: tr("Estimate Qty"),
    },
    used_price: {
      label: tr("Used Total"),
      cell: (row: any) => dollarFormatter(row.used_qty * row.price),
    },
    used_qty: {
      label: tr("Used Qty"),
      cell: (row: any) => {
        const color =
          row.used_qty === 0
            ? "text-slate-400"
            : row.used_qty <= row.estimate_qty
            ? "text-green-600"
            : "text-red-600";
        return <span className={`${color}`}>{row.used_qty}</span>;
      },
    },
    price: {
      label: tr("Remaining"),
      cell: (row: any) =>
        dollarFormatter(
          Math.max(0, row.estimate_qty - row.used_qty) * row.price
        ),
    },
  };

  const userColumns = {
    fk_user_id: {
      label: "ID",
    },
    user: {
      label: tr("Crew"),
      width: "100%",
      cell: (row: any) => (
        <Avatar showLabel size="small" user_id={row.fk_user_id}>
          {row.user}
        </Avatar>
      ),
    },
    sum: {
      label: tr("Production Total"),
      cell: (row: any) => dollarFormatter(row.sum),
    },
  };

  // let prodTotal =
  //   query.data?.users?.reduce((sum, user) => sum + user.sum, 0) || 0;

  let prodTotal = userData.reduce((sum:any, user:any) => sum + user.sum, 0) || 0;
  let estimateTotal =
    query.data?.items?.reduce(
      (sum, item) => sum + item.estimate_qty * item.price,
      0
    ) || 0;
  let completion = (prodTotal / (estimateTotal || 1)) * 100;

  return (
    <div className="space-y-8">
      <div className="divide-y">
        <div className="py-2">
          <h2>{tr("Crew Production")}</h2>
        </div>
        <div className="py-2">
          <TableComponent columns={userColumns} data={userData} />
        </div>
      </div>
      <div>
        <div className="py-2">
          <h2>{tr("Completed")}</h2>
        </div>
        <div className="w-full h-[5px] bg-slate-200 rounded-full relative overflow-hidden">
          <div
            className={`absolute h-full bg-[#ff4f00]`}
            style={{ width: `${completion}%` }}
          />
        </div>
        {dollarFormatter(prodTotal)} / {dollarFormatter(estimateTotal)}(
        {completion.toFixed(2)}%)
      </div>
      <div className="divide-y">
        <div className="py-2 flex justify-between">
          <h2>{tr("Items")}</h2>
          <div className="space-x-2">
            <Switch checked={hide} onChange={(e) => setHide(e)} />
            <span>{tr("Hide Non-Production Items")}</span>
          </div>
        </div>
        <TableComponent
          columns={columns}
          data={query.data?.items?.filter(
            (item) => !hide || item.estimate_qty !== 0
          )}
        />
      </div>
    </div>
  );
}
